import React, { Component, ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Typography,
} from '@mui/material';
import { ErrorOutlineOutlined, RefreshOutlined } from '@mui/icons-material';
import { faro } from '@grafana/faro-web-sdk';
import { useErrorStore } from '../stores/errorStore';
import { systemColors } from '../theme/colors';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  // Drop the error when any entry changes, so navigation can show the children again.
  resetKeys?: unknown[];
  // Runs before the children re-render. Try Again throws again if this leaves the bad state in place.
  onReset?: () => void;
}

const resetKeysChanged = (prev: unknown[] = [], next: unknown[] = []) =>
  prev.length !== next.length ||
  prev.some((key, index) => !Object.is(key, next[index]));

interface ErrorFallbackProps {
  error: Error | null;
  onRetry: () => void;
  onReload: () => void;
}

/**
 * Default error fallback component
 */
function ErrorFallback({ error, onRetry, onReload }: ErrorFallbackProps) {
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Card sx={{ textAlign: 'center', p: 3 }}>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <ErrorOutlineOutlined
              sx={{
                fontSize: 64,
                color: systemColors.errorRed,
                mb: 2,
              }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              We&apos;re sorry, but an unexpected error occurred. Please try
              again or reload the page.
            </Typography>

            {isDevelopment && error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 'bold' }}
                >
                  Error Details (Development Mode):
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 200,
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {error.message}
                  {error.stack && (
                    <>
                      {'\n\nStack trace:\n'}
                      {error.stack}
                    </>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={onRetry}
            size="large"
          >
            Try Again
          </Button>
          <Button variant="contained" onClick={onReload} size="large">
            Reload Page
          </Button>
        </CardActions>
      </Card>
    </Container>
  );
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire app
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError } = this.props;
    this.setState({
      error,
    });

    // A caught error only reaches console.error, and Faro has captureConsole off.
    // faro.api is unset when Faro is not initialized.
    faro.api?.pushError(error, {
      context: { componentStack: errorInfo.componentStack ?? '' },
    });

    // Log to error store
    const errorStore = useErrorStore.getState();
    errorStore.handleApiError(error, 'load this page');

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      // Error information available for debugging but not logged to console
      // Error: error, ErrorInfo: errorInfo
    }
  }

  componentDidUpdate(
    prevProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState,
  ) {
    // Skip the update that first caught the error. resetKeys have not changed yet.
    const { resetKeys } = this.props;
    const { hasError } = this.state;
    if (
      hasError &&
      prevState.hasError &&
      resetKeysChanged(prevProps.resetKeys, resetKeys)
    ) {
      this.handleRetry();
    }
  }

  handleRetry = () => {
    const { onReset } = this.props;
    onReset?.();
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render() {
    const { hasError, error } = this.state;
    const { fallback, children } = this.props;
    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <ErrorFallback
          error={error}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return children;
  }
}

/**
 * Higher-order component for wrapping components with error boundary
 */
export const withErrorBoundary = <P extends object>(
  ComponentToWrap: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void,
) => {
  function WrappedComponent(props: P) {
    return (
      <ErrorBoundary fallback={fallback} onError={onError}>
        {}
        <ComponentToWrap {...props} />
      </ErrorBoundary>
    );
  }

  WrappedComponent.displayName = `withErrorBoundary(${ComponentToWrap.displayName || ComponentToWrap.name})`;
  return WrappedComponent;
};

/**
 * Hook for using error boundary in functional components
 * This doesn't actually create a boundary but provides a way to trigger error boundary
 */
export const useErrorBoundary = () => {
  const [, setError] = React.useState<Error | null>(null);

  const captureError = React.useCallback((error: Error) => {
    setError(() => {
      throw error;
    });
  }, []);

  return { captureError };
};
